// BulkActionBar — Phase 6b sticky footer that appears when Kimi has selected
// one or more threads on Standup. Offers the three actions Kimi locked in
// during planning: Resolve, Snooze (with preset durations), and Follow up
// (status='awaiting' without replying — the thread lands in Circle back).
//
// UX notes:
// - Fixed to the bottom of the viewport, full width, slides up into view
//   when count > 0. FooterBand sits above it in normal flow, so when the
//   bar is hidden the keyboard-shortcut band is the last thing on screen.
// - Selection count is the hero element. "Cancel" clears the selection.
// - Snooze popover uses the shared SNOOZE_PRESETS so labels/durations match
//   the single-thread SnoozeMenu exactly. Custom datetime picking is NOT
//   supported in bulk — if you need a specific wake time, do it per-thread.
// - Busy state disables every action button; the bar stays pinned so the
//   count doesn't disappear mid-request.
//
// Polish #24 additions:
// - "Mark as spam" now routes through a ConfirmDialog. Bulk-spam is the
//   only action here that hides threads from the Pile, and getting it
//   wrong at 8x or 20x is a nightmare to recover from.
// - "Resolve" also confirms when resolving 5+ threads at once. Below that
//   it fires directly — one or two-at-a-time resolves are the common case.
// - The bar now optionally renders a "Select all N" button when the parent
//   passes an `onSelectAll` handler and a `selectAllCount`. Makes it
//   trivial to escalate from "just these two" to "everything in the Pile"
//   without clicking every card.
import { useEffect, useRef, useState } from 'react';
import { SNOOZE_PRESETS } from '@md/shared';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';

type BulkActionBarProps = {
  count: number;
  busy: boolean;
  onResolve: () => void;
  onSnooze: (minutes: number) => void;
  onFollowUp: () => void;
  onSpam: () => void;
  onCancel: () => void;
  /**
   * When provided, the bar renders a "Select all N" button so Kimi can
   * bulk-expand her current selection to every card currently in view.
   * Parent (Standup) computes N from The Pile length and passes it in.
   */
  onSelectAll?: () => void;
  /** Total candidate count for the "Select all N" label. */
  selectAllCount?: number;
};

// Threshold at or above which a bulk Resolve asks for confirmation.
// Below this we fire directly since 1-2 resolves is the frequent case.
const RESOLVE_CONFIRM_THRESHOLD = 5;

export function BulkActionBar({
  count,
  busy,
  onResolve,
  onSnooze,
  onFollowUp,
  onSpam,
  onCancel,
  onSelectAll,
  selectAllCount,
}: BulkActionBarProps) {
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const popRef = useRef<HTMLDivElement | null>(null);
  const [confirm, setConfirm] = useState<
    | null
    | {
        kind: 'spam' | 'resolve';
        title: string;
        body: string;
        confirmLabel: string;
        danger: boolean;
        onConfirm: () => void;
      }
  >(null);

  // Close the snooze popover on outside-click / Escape.
  useEffect(() => {
    if (!snoozeOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (popRef.current && e.target instanceof Node && !popRef.current.contains(e.target)) {
        setSnoozeOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSnoozeOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [snoozeOpen]);

  // Wrappers that route destructive actions through the confirm dialog
  // when the stakes are high. The parent's handlers are still the only
  // ones that call the mutation — we just defer the call until Kimi
  // confirms.
  const requestSpam = () => {
    const noun = count === 1 ? 'thread' : 'threads';
    setConfirm({
      kind: 'spam',
      title: `Mark ${count} ${noun} as spam?`,
      body:
        count === 1
          ? `This thread will be hidden from the Pile. You can reveal it later with the "likely-spam hidden" toggle.`
          : `These ${count} threads will be hidden from the Pile. You can reveal them later with the "likely-spam hidden" toggle.`,
      confirmLabel: 'Mark as spam',
      danger: true,
      onConfirm: () => {
        setConfirm(null);
        onSpam();
      },
    });
  };

  const requestResolve = () => {
    if (count < RESOLVE_CONFIRM_THRESHOLD) {
      onResolve();
      return;
    }
    setConfirm({
      kind: 'resolve',
      title: `Resolve ${count} threads?`,
      body: `They'll move to "For the record" and stop bothering you. You can reopen any one of them from Search if you change your mind.`,
      confirmLabel: 'Resolve all',
      danger: false,
      onConfirm: () => {
        setConfirm(null);
        onResolve();
      },
    });
  };

  if (count === 0) return null;

  return (
    <div
      role="region"
      aria-label="Bulk actions"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 40,
        background: 'hsl(var(--stone))',
        borderTop: '1px solid hsl(var(--ink) / 0.12)',
        boxShadow: '0 -6px 20px hsl(var(--ink) / 0.08)',
      }}
    >
      <div
        className="wrap"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          padding: '14px 24px',
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontWeight: 600,
            fontSize: 15,
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 28,
              height: 28,
              padding: '0 8px',
              borderRadius: 999,
              background: 'hsl(var(--monstera))',
              color: 'hsl(var(--stone))',
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {count}
          </span>
          selected
        </div>

        {onSelectAll &&
        typeof selectAllCount === 'number' &&
        selectAllCount > count ? (
          <button
            type="button"
            onClick={onSelectAll}
            disabled={busy}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'hsl(var(--ink) / 0.7)',
              cursor: busy ? 'not-allowed' : 'pointer',
              fontSize: 13,
              padding: '6px 10px',
              textDecoration: 'underline',
              textUnderlineOffset: 2,
            }}
            title="Select every thread currently in the Pile"
          >
            Select all {selectAllCount}
          </button>
        ) : null}

        <div style={{ flex: 1 }} />

        <button
          type="button"
          className="btn"
          onClick={requestResolve}
          disabled={busy}
          style={buttonStyle}
        >
          Resolve
        </button>

        <div style={{ position: 'relative' }} ref={popRef}>
          <button
            type="button"
            className="btn"
            onClick={() => setSnoozeOpen((v) => !v)}
            disabled={busy}
            aria-haspopup="menu"
            aria-expanded={snoozeOpen}
            style={buttonStyle}
          >
            Snooze ▾
          </button>
          {snoozeOpen ? (
            <div
              role="menu"
              style={{
                position: 'absolute',
                right: 0,
                bottom: 'calc(100% + 8px)',
                background: 'hsl(var(--stone))',
                border: '1px solid hsl(var(--ink) / 0.12)',
                borderRadius: 10,
                boxShadow: '0 10px 30px hsl(var(--ink) / 0.12)',
                minWidth: 200,
                padding: 6,
              }}
            >
              {SNOOZE_PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  role="menuitem"
                  disabled={busy}
                  onClick={() => {
                    setSnoozeOpen(false);
                    onSnooze(p.minutes);
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '10px 12px',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: 6,
                    cursor: busy ? 'not-allowed' : 'pointer',
                    fontSize: 14,
                    color: 'hsl(var(--ink))',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background =
                      'hsl(var(--ink) / 0.04)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <button
          type="button"
          className="btn"
          onClick={onFollowUp}
          disabled={busy}
          style={buttonStyle}
          title="Mark as awaiting a reply without sending — goes to Circle back"
        >
          Follow up
        </button>

        <button
          type="button"
          className="btn"
          onClick={requestSpam}
          disabled={busy}
          style={{
            ...buttonStyle,
            color: 'hsl(var(--clay))',
            borderColor: 'hsl(var(--clay) / 0.35)',
          }}
          title="Mark selected threads as spam — hidden from the Pile"
        >
          Mark as spam
        </button>

        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'hsl(var(--ink) / 0.6)',
            cursor: busy ? 'not-allowed' : 'pointer',
            fontSize: 14,
            padding: '8px 10px',
            textDecoration: 'underline',
            textUnderlineOffset: 2,
          }}
        >
          Cancel
        </button>
      </div>
      <ConfirmDialog
        open={confirm !== null}
        title={confirm?.title ?? ''}
        body={confirm?.body ?? ''}
        confirmLabel={confirm?.confirmLabel ?? 'Confirm'}
        danger={confirm?.danger ?? false}
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => confirm?.onConfirm()}
      />
    </div>
  );
}

const buttonStyle: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 8,
  border: '1px solid hsl(var(--ink) / 0.14)',
  background: 'hsl(var(--stone))',
  color: 'hsl(var(--ink))',
  fontWeight: 500,
  fontSize: 14,
  cursor: 'pointer',
};
